/** @format */

import {format, subDays} from 'date-fns';

import {ru} from 'date-fns/locale';

import {MultilineChartInterface, PointName} from 'components/charts/multiline-chart/model/common';

import {YalperFunnelGoalStat} from 'graphql/generated/types';

import {Chart} from 'components/charts/multiline-chart/model/charts';

import {generateGraphPointName} from 'helpers/miscHelpers';

import {divideBySpaces} from 'helpers/numberHelpers';

import {
  TooltipData,
  TooltipDataList,
  TooltipRow,
  TooltipValue,
} from 'components/charts/multiline-chart/model/tooltip';

import {Discreteness} from 'components/PeriodSelector/model';

import {ChartIds} from '../../../models/chartIds';
import {WidgetSettingsList} from '../../../models/spaces';
import {WidgetBlock} from '../../../models/widgets';
import {GraphComplexValuesList} from '../../../models/common';
import {buildCrSeries} from './crBuildSeries';
import {finalizeCrSeries} from './crFinalizeSeries';
import {alignCrForecastWithReference} from './alignCrForecast';

import {WIDGETS_LIST} from '../../../constants/widgets';

import getTooltipPointForecast from '../../../mapping/tooltip/getTooltipPointForecast';
import {getFunnelForecastRows, getFunnelTooltipValue} from './tooltipFunnel';
import {AnalyticGoalFromQuery} from '../../../models/api';
import {getTooltipDelta} from '../../../mapping/tooltip/getTooltipDelta';
import {ByGoalDate, getLatestDateIndex} from './getLatestDateIndex';

// Костыль-фикс для кода ниже.
// Исправляет баг, когда в тултип попадают лишние primary/secondary.

const clearFutureFunnelValues = (
  data: TooltipDataList<ChartIds>,
  yesterdayIndex: number,
  funnelIds: ChartIds[],
): TooltipDataList<ChartIds> => {
  const result: TooltipDataList<ChartIds> = {};
  const funnelIdsSet = new Set<ChartIds>(funnelIds);

  const entries = Object.entries(data) as [PointName, TooltipData<ChartIds>][];

  for (let i = 0; i < entries.length; i++) {
    const [pointName, tooltipData] = entries[i];
    // 1. Берём как есть, если индекс не превышает yesterdayIndex
    if (i <= yesterdayIndex) {
      result[pointName] = tooltipData;
      continue;
    }

    // 2. Если rows нет — ничего не делаем
    if (!tooltipData.rows) {
      result[pointName] = tooltipData;
      continue;
    }

    // 3. Чистим только указанные funnelIds
    const newRows: TooltipRow<ChartIds>[] = tooltipData.rows.map(row => {
      if (!funnelIdsSet.has(row.chartId)) {
        // этот ряд не относится к нужным chartId — возвращаем как есть
        return row;
      }

      const oldValue = row.value;

      const newValue: TooltipValue = {
        // если primary существует — обнуляем, иначе оставляем как есть
        primary: oldValue.primary !== undefined ? '' : oldValue.primary,
        // если secondary есть (включая пустую строку) — делаем пустую строку
        ...(oldValue.secondary !== undefined ? {secondary: ''} : {}),
      };

      return {
        ...row,
        value: newValue,
      };
    });

    result[pointName] = {
      ...tooltipData,
      rows: newRows,
    };
  }

  return result;
};

// Источники прогнозов для A/B
const resolveChartId = (
  analyticGoals: ReadonlyArray<AnalyticGoalFromQuery>,
  key: number | string,
): ChartIds | undefined => {
  if (key === 'clicks' || key === 'impressions') return key as ChartIds;
  const goal = analyticGoals.find(g => String(g.id) === String(key));
  return goal ? (goal.name as ChartIds) : undefined;
};

interface Options {
  chartsData: MultilineChartInterface<ChartIds>;
  widgetsSettings: WidgetSettingsList;
  funnelGoalStats: ReadonlyArray<YalperFunnelGoalStat>;
  widgetBlocks: WidgetBlock[];
  graphComplexValues: GraphComplexValuesList;
  analyticGoals: ReadonlyArray<AnalyticGoalFromQuery>;
  rechartsDates: string[];
  discreteness: Discreteness;
}

const getChartsDataWithCR = ({
  chartsData,
  widgetsSettings,
  widgetBlocks,
  funnelGoalStats,
  analyticGoals,
  graphComplexValues,
  rechartsDates,
  discreteness,
}: Options): MultilineChartInterface<ChartIds> => {
  if (!funnelGoalStats?.length) {
    return chartsData;
  }

  const accumulatedPoints = rechartsDates.reduce<{name: string; date: string}[]>(
    (acc, cur, index) => {
      const point = generateGraphPointName(index);
      return [
        ...acc,
        {
          name: point,
          date: cur,
        },
      ];
    },
    [],
  );

  const orderedPointNames = accumulatedPoints.map(d => d.name);

  const byGoalDate: ByGoalDate = {};

  funnelGoalStats.forEach(s => {
    if (!byGoalDate[s.goalId]) byGoalDate[s.goalId] = {};
    const key = s.date; // строгий ключ ISO
    byGoalDate[s.goalId][key] = (byGoalDate[s.goalId][key] || 0) + (s.conversions || 0);
  });

  const funnelColor = widgetBlocks.find(b => b.id === 'funnel')?.color.main || '#ffffff';

  const newCharts: Chart<ChartIds>[] = [...chartsData.charts];

  const crIds = Object.keys(widgetsSettings).filter(id =>
    id.startsWith('funnel_cr_'),
  ) as ChartIds[];

  const yesterdayIndex = getLatestDateIndex(byGoalDate, rechartsDates);

  for (let i = 0; i < crIds.length; i++) {
    const id = crIds[i];
    const custom = widgetsSettings[id]?.custom;

    if (!custom?.aGoalId || !custom?.bGoalId) {
      continue;
    }

    const aId = custom.aGoalId;
    const bId = custom.bGoalId;
    const isABasicMetric = aId === 'clicks' || aId === 'impressions' || aId === 'shows';
    const isBBasicMetric = bId === 'clicks' || bId === 'impressions' || bId === 'shows';

    const aChart = resolveChartId(analyticGoals, aId)
      ? newCharts.find(c => c.chartId === resolveChartId(analyticGoals, aId))
      : undefined;
    const bChart = resolveChartId(analyticGoals, bId)
      ? newCharts.find(c => c.chartId === resolveChartId(analyticGoals, bId))
      : undefined;

    const crDataPoints = buildCrSeries({
      orderedPointNames,
      aId,
      bId,
      graphComplexValues,
      aChart,
      bChart,
      yesterdayIndex,
    });

    const completedCrDataPoints = finalizeCrSeries(crDataPoints, yesterdayIndex);

    // Выравниваем прогноз CR (Клики/Показы) по CTR
    const isCtrPair =
      isABasicMetric &&
      isBBasicMetric &&
      ((aId === 'clicks' && (bId === 'impressions' || bId === 'shows')) ||
        (bId === 'clicks' && (aId === 'impressions' || aId === 'shows')));
    const isClicksOverImpressions =
      isCtrPair && aId === 'clicks' && (bId === 'impressions' || bId === 'shows');
    // kept for clarity of orientation calculation
    const ctrChart = newCharts.find(c => c.chartId === 'ctr');
    const completedAligned =
      isCtrPair && ctrChart
        ? alignCrForecastWithReference({
            points: completedCrDataPoints,
            referenceChart: ctrChart,
            yesterdayIndex,
            orientation: isClicksOverImpressions ? 'direct' : 'inverse',
          })
        : completedCrDataPoints;

    const newChart: Chart<ChartIds> = {
      chartId: id,
      label: custom.label || 'CR, %',
      color: funnelColor,
      chartsData: completedAligned,
    };

    newCharts.push(newChart);
  }

  // Обогащаем тултипы для CR виджетов
  const updatedTooltipDataList = {...chartsData.tooltipDataList};

  for (let i = 0; i < crIds.length; i++) {
    const id = crIds[i];

    const custom = widgetsSettings[id]?.custom;

    if (!custom?.aGoalId || !custom?.bGoalId) {
      continue;
    }

    const label = custom.label || 'CR, %';

    for (let j = 0; j < orderedPointNames.length; j++) {
      const pointName = orderedPointNames[j];
      const tooltipData = updatedTooltipDataList[pointName];

      if (!tooltipData) {
        continue;
      }

      const existingRows = tooltipData.rows || [];
      const rowsWithoutCr = existingRows.filter(row => row.chartId !== id);

      // Находим соответствующие данные из finalChartsData
      const chart = newCharts.find(c => c.chartId === id);
      const point = chart?.chartsData[j];

      if (!point || !chart) {
        continue;
      }

      const primaryValue = point.primary ?? 0;
      const value: {primary: string} = {
        primary: `${primaryValue} %`,
      };

      // Добавим строки прогноза (ср./мин./макс.) для CR
      const widget = WIDGETS_LIST[id];
      const yesterdayHuman = format(subDays(new Date(), 1), 'd MMMM yyyy', {
        locale: ru,
      });
      const forecastRows = getTooltipPointForecast({
        data: point,
        token: widget.token,
        roundNumberBy: widget.roundNumberBy,
        hideForecast: tooltipData.date === yesterdayHuman,
      });

      // На прогнозных точках скрываем основное значение, оставляя только строки прогноза
      const isForecastCrPoint =
        point.primary === undefined && (point.average !== undefined || point.range !== undefined);
      if (forecastRows.length && (isForecastCrPoint || primaryValue === 0)) {
        value.primary = '';
      }

      const isNeedDelta = (discreteness === 'week' || discreteness === 'month') && j > 0;
      const prevPoint = isNeedDelta ? chart.chartsData[j - 1] : undefined;

      const crRow: TooltipRow<ChartIds> = {
        chartId: id,
        label,
        value,
        ...(forecastRows.length ? {extraRows: forecastRows} : {}),
      };

      const delta = getTooltipDelta({
        current: point,
        previous: prevPoint,
        roundNumberBy: 1,
        token: '%',
      });

      if (delta) {
        crRow.delta = delta;
      }

      const newRows = [...rowsWithoutCr, crRow];

      updatedTooltipDataList[pointName] = {
        ...tooltipData,
        rows: newRows,
      };
    }
  }

  // Синхронизация значений тултипа для воронки с активным методом (день/сумма)
  const goalChartIds = analyticGoals.map(g => g.name as ChartIds);
  const yesterdayHumanForTooltip = format(subDays(new Date(), 1), 'd MMMM yyyy', {locale: ru});

  for (let i = 0; i < goalChartIds.length; i++) {
    const goalChartId = goalChartIds[i];
    const chart = newCharts.find(c => c.chartId === goalChartId);
    if (!chart) {
      continue;
    }

    for (let j = 0; j < orderedPointNames.length; j++) {
      const pointName = orderedPointNames[j];

      const tooltipData = updatedTooltipDataList[pointName];
      if (!tooltipData) {
        continue;
      }

      const point = chart.chartsData[j];
      if (!point) {
        continue;
      }

      const isForecastZone =
        j > yesterdayIndex && point.primary === undefined && point.secondary === undefined;
      const prev = j > 0 ? chart.chartsData[j - 1] : undefined;

      const forecastRows = getFunnelForecastRows(
        point,
        WIDGETS_LIST[goalChartId].token,
        WIDGETS_LIST[goalChartId].roundNumberBy || 0,
        tooltipData.date === yesterdayHumanForTooltip,
        isForecastZone,
        pointName,
        prev,
      );

      const existingRows = tooltipData.rows || [];

      const newRows = existingRows.map(row => {
        if (row.chartId !== goalChartId) {
          return row;
        }
        const base = getFunnelTooltipValue(point, prev);
        const baseValue: TooltipValue = {
          ...row.value,
          primary: divideBySpaces(base.primary),
        };
        // На прогнозной зоне скрываем основное значение
        if (isForecastZone && forecastRows.length) {
          baseValue.primary = '';
        }

        return {
          ...row,
          value: baseValue,
          ...(forecastRows.length
            ? {extraRows: [...(row.extraRows || []), ...forecastRows]}
            : row.extraRows
              ? {extraRows: row.extraRows}
              : {}),
        };
      });

      updatedTooltipDataList[pointName] = {
        ...tooltipData,
        rows: newRows,
      };
    }
  }

  const fixedTooltip = clearFutureFunnelValues(
    updatedTooltipDataList,
    yesterdayIndex,
    goalChartIds,
  );

  return {
    ...chartsData,
    charts: newCharts,
    tooltipDataList: fixedTooltip,
  };
};

export {getChartsDataWithCR};
